import { useEffect } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { NOTIFICATION_PAGE_SIZE } from '@proref/schemas';
import type { NotificationPrefs } from '@proref/types';
import type { UpdateNotificationPrefsInput } from '@proref/schemas';
import {
  listNotifications,
  markNotificationsRead,
  getNotificationPrefs,
  updateNotificationPrefs,
  type NotificationPage,
} from '../services/notifications.service';
import { connectNotificationStream } from '../lib/notification-stream';

// Every notification query hangs off this prefix so one invalidate refreshes the bell, the badge
// and the full-page list together — they must never disagree about what is unread.
const NOTIFICATIONS_KEY = ['notifications'] as const;

// How many entries the topbar dropdown shows. Small on purpose: the dropdown is a glance, and
// "See all" is one click away.
export const BELL_PAGE_SIZE = 10;

// Safety net under the live stream. Streams drop, proxies buffer, laptops sleep — this guarantees
// nothing is ever permanently missed, and it means the bell works fully before any streaming code
// is reached (or on a browser where the stream can't run at all).
const POLL_MS = 5 * 60_000;

// The topbar dropdown: the newest entries plus the unread count that drives the badge.
//
// The count comes from the same response as the list rather than its own endpoint, so the badge
// can never say "3" over a dropdown showing none — and the poll below stays one request.
export function useNotificationBellQuery(enabled = true) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'bell'],
    queryFn: () => listNotifications({ limit: BELL_PAGE_SIZE }),
    enabled,
    // Overrides the app-wide 5-minute staleTime and refetchOnWindowFocus:false. Both defaults are
    // right for content that changes when the user changes it; a notification arrives from
    // somewhere else entirely, so "what's waiting for me" must never be served stale, and coming
    // back to the tab is exactly the moment to check.
    staleTime: 0,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

// The /notifications page — the same list, paged.
export function useNotificationsListQuery(unreadOnly: boolean) {
  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'list', { unreadOnly }],
    queryFn: ({ pageParam }) =>
      listNotifications({
        limit: NOTIFICATION_PAGE_SIZE,
        cursor: pageParam,
        unreadOnly,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: NotificationPage) => last.meta.next_cursor,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

// Mark a set of notifications — or the whole inbox — as read.
//
// No optimistic write and no success toast. Marking read is a side effect of reading, not an action
// the user is waiting on a verdict for; the row losing its unread dot after the refetch is all the
// confirmation there is to give. Errors are swallowed for the same reason: a failed mark-read
// leaves the badge as it was, which is the honest state, and a toast about it would be noise on an
// action the user never consciously took.
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[] } | { all: true }) => markNotificationsRead(input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

// Hold the live stream open for as long as the caller is mounted, refreshing the notification
// queries whenever something arrives.
//
// It INVALIDATES rather than splicing the pushed payload into the cache. Writing it in by hand
// would mean reimplementing the server's ordering, its keyset paging and its unread count on the
// client, and getting any of the three subtly wrong shows up as a duplicated or silently missing
// entry. The refetch is one small request against an indexed query, and it keeps the server the
// single source of truth for what the list actually contains.
export function useNotificationStream(enabled = true) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    return connectNotificationStream({
      onNotification: () => {
        void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      },
    });
  }, [enabled, qc]);
}

// The six category switches behind /settings.
//
// Kept OUT of the ['notifications'] prefix on purpose: preferences change only when this user
// changes them, so they must not be dragged into the refetch that every arriving notification
// triggers.
export function useNotificationPrefsQuery() {
  return useQuery({ queryKey: ['notification-prefs'], queryFn: getNotificationPrefs });
}

// Flip one category's switch — OPTIMISTICALLY, and with no success toast.
//
// A toggle's own state flip IS the confirmation; a toast arriving after the round-trip reads as
// lag on a control that already moved. The error path rolls the switch back, and the caller
// raises the toast there — the only moment the user needs telling.
export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  const key = ['notification-prefs'] as const;

  return useMutation({
    mutationFn: (patch: UpdateNotificationPrefsInput) => updateNotificationPrefs(patch),
    onMutate: async (patch) => {
      // Cancel in flight refetches, or a slow response could land on top of the optimistic value.
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<NotificationPrefs>(key);
      qc.setQueryData<NotificationPrefs>(key, (old) => {
        if (!old) return old;
        const next = { ...old };
        for (const [category, channels] of Object.entries(patch)) {
          const cat = category as keyof NotificationPrefs;
          next[cat] = { ...next[cat], ...channels };
        }
        return next;
      });
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      // Restore the exact pre-click state — the switch snaps back to what the server still holds.
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSuccess: (resolved) => {
      // The API answers with the full resolved set, so take it rather than refetching: it is
      // authoritative about how the patch merged with what was already stored.
      qc.setQueryData(key, resolved);
    },
  });
}
